import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { el } from "./locales/el";
import { en } from "./locales/en";
import { fr } from "./locales/fr";

export type Language = "el" | "en" | "fr";
export type TranslationParams = Record<string, number | string>;

const STORAGE_KEY = "puls.web.language";
const supportedLanguages: Language[] = ["el", "en", "fr"];
const translations: Record<Language, Record<string, string>> = { el, en, fr };

type I18nContextValue = {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  supportedLanguages: Language[];
  t: (key: string, params?: TranslationParams) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function normalizeLanguage(language?: string | null): Language | null {
  if (!language) return null;
  const normalized = language.toLowerCase().slice(0, 2);
  return supportedLanguages.includes(normalized as Language) ? (normalized as Language) : null;
}

function getInitialLanguage(): Language {
  if (typeof window === "undefined") return "el";

  const savedLanguage = normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
  if (savedLanguage) return savedLanguage;

  const browserLanguage = normalizeLanguage(window.navigator.language);
  return browserLanguage ?? "el";
}

function formatTranslation(value: string, params?: TranslationParams) {
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (match, key) => String(params[key] ?? match));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(getInitialLanguage);

  const value = useMemo<I18nContextValue>(() => {
    const setLanguage = (language: Language) => {
      setCurrentLanguage(language);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, language);
      }
    };

    const t = (key: string, params?: TranslationParams) => {
      const rawValue = translations[currentLanguage][key] ?? translations.el[key] ?? key;
      return formatTranslation(rawValue, params);
    };

    return {
      currentLanguage,
      setLanguage,
      supportedLanguages,
      t
    };
  }, [currentLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
}
